import { useState, useEffect, useMemo, Fragment, useCallback, useRef } from "react";
import '@salesforce-ux/design-system/assets/styles/salesforce-lightning-design-system.min.css';
import PresenceIndicator from "./components/PresenceIndicator";
import PhonePanel from "./components/PhonePanel";
import { IncomingNotification } from "./components/IncomingNotification";
import { CallHistoryProvider } from "./context/CallHistoryContext";
import HistoryList from './components/CallHistory/HistoryList';
import {
  Text,
  Button,
  Card,
  CardHeader,
  CardPreview,
  CardFooter,
  Tooltip,
  //START: tab
  TabList,
  Tab,
  Divider,
  //END: tab
  //START: list
  Body1,
  makeStyles,
  mergeClasses,
  Toaster,
  useToastController,
  Toast,
  ToastTitle,
  tokens,
  //END: list
} from '@fluentui/react-components';
import { 
  Call16Filled, SignOutFilled,
} from "@fluentui/react-icons";
import { setSoftphonePanelLabel, setSoftphoneItemLabel, setSoftphonePanelHeight, fetchAndStoreUserLocale, loadCallCenterSettings, getListOfWhatIdObjWithActivityEnabled, getWhatIdListOnTask, setSoftphoneItemIcon } from "./utils/acs.salesforce";
import { getLoginMessage, getSoftphoneLabel } from "./utils/callCenterSettings";
import useACSClient from "./hooks/useACSClient";
import ActiveCallScreen from "./components/ActiveCall/ActiveCallScreen";
import SignIn from "./components/SignIn";
import {SwitchCall} from "./components/Notification/SwithCall";
import { useErrorToast } from "./hooks/useErrorToast";
import { checkSession, completeSessionTransfer, refreshSession, logout as sessionLogout, getAcsToken, authenticatedFetch } from "./utils/session";

function App() {
  //for tabs
  const [selectedTab, setSelectedTab] = useState("phone");  //inbound
  //for list
  const styles = useStyles();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState(null);
  const [accountInfo, setAccountInfo] = useState(null);
  const [userObjectId, setUserObjectId] = useState(null);
  const [loginMessage, setLoginMessage] = useState(null);
  const [softphoneLabel, setSoftphoneLabel] = useState("Unaric Voice for Teams");
  const toasterId = "app-toaster";
    const { dispatchToast } = useToastController(toasterId);

  const {
    call,
    transferredCall,
    incomingCall,
    callMap, incomingCallMap, 
    upsertCallMap,
    setActiveConnectedCallId, 
    activeConnectedCallId,
    upsertIncomingCallMap, 
    incomingCallMapRef, 
    callMapRef, 
    activeConnectedCallIdRef,
    lastEntityIdRef, 
    assignMetaRef,
    deletedAssignedRecIdRef,
    setDeletedAssignedRecId,
	  setWrapupConnectedCallId, 
    wrapupConnectedCallId, 
    wrapupConnectedCallIdRef, 
    wrapupDone, 
    wrapupEdit,
    callState,
  isSwitchingCall,
    errorMessage,
    notes,
    logout,
    placeCall,
    acceptCall,
    hangUp,
    //isMuted,
    mute,
    unmute,
    setNotes,
  	onNotesInputChange,
	callResult, 
    setCallResult, 
    callResultGroup, 
    setCallResultGroup,
  	onCallResultInputChange, 
    onCallResultGroupInputChange,
    callerInfo,
    getActiveCall,
    switchActiveCall,
    blindTransfer, 
    consultedTransfer,
    cancelTransfer,
    callTransferState,
    onAnotherDevice,
    //isHold,
    onHold,
    onResume,
    handleDtmf,
    transferorInfo,
    pickCallId,
    activeCallDet,
    callStartTimeRef,
  } = useACSClient(token, accountInfo);

  const [recToConfirmBeforeAssignment, setRecToConfirmBeforeAssignment] = useState({ recordId: null, recordName: null, objectType: null });
  const showJoinNow = useMemo(() => {
    return pickCallId !== activeCallDet?.[0]?.callId;
  }, [pickCallId, activeCallDet]);

  const ErrorToaster = useErrorToast(errorMessage);
  console.log("callerInfo", callerInfo,'onAnotherDevice',onAnotherDevice);

  const handleDefaultAssignment = (rec) => {
    const whoIdPrefixes = ["003", "00Q"];
    if(whoIdPrefixes.includes(rec.recordId.substring(0, 3))) {
      upsertCallMap(activeConnectedCallIdRef?.current, {whoId: rec.recordId, whoName: rec?.recordName, whoType: rec?.objectType});
    } else {
      upsertCallMap(activeConnectedCallIdRef?.current, {whatId: rec.recordId, whatName: rec?.recordName, whatType: rec?.objectType});
    }
  }
  //once on mount - check session instead of localStorage
  useEffect(() => {
    const initSession = async () => {
      try {
        // Check if user has valid session
        const { authenticated, user } = await checkSession();
        
        if (authenticated && user) {
          setIsAuthenticated(true);
          setUserObjectId(user.userObjectId);
          setAccountInfo({ name: user.displayName, username: user.username, upn: user.upn, tenantId: user.tenantId });
          setSoftphoneItemIcon("call");
          
          // Get ACS token from session for initializing ACS SDK
          const acsTokenData = await getAcsToken();
          if (acsTokenData) {
            setToken({
              token: acsTokenData.token,
              expiresOn: acsTokenData.expiresOn,
            });
          }
        }
      } catch (err) {
        console.error("Error checking session:", err);
      }
    };

    initSession();
    loadCallCenterSettings({
      onLoaded: () => {
        setLoginMessage(getLoginMessage());
        setSoftphoneLabel(getSoftphoneLabel());
      },
    });
    fetchAndStoreUserLocale();
    getListOfWhatIdObjWithActivityEnabled();
    //setSoftphonePanelHeight({heightPX: 540}); //w.r.t adapter xml 560 //to remove vertical scoll, change also in adapter
  	
    // Track active SF record
    try {
      sforce.opencti.getPageInfo?.({
        callback: (res) => {
          const id = res?.result?.objectId || res?.result?.recordId;
          if (id) lastEntityIdRef.current = id;
          else lastEntityIdRef.current = null;
        },
      });

      sforce.opencti.onNavigationChange?.({
        listener: (evt) => {
          const id = evt?.objectId || evt?.recordId;
          if (id) lastEntityIdRef.current = id;
          else lastEntityIdRef.current = null;

          let deletedAssignedRecId = deletedAssignedRecIdRef?.current;
          const donotAssignPrefixesToTask = ["00T", "00U", "02s", "01Z", "00O", "00l"]; //02s = EmailMessage, 01Z -> Dashboard, 00O -> Report, 00l -> Folder
          const actEnabledWhatIdPrefixes = getWhatIdListOnTask();
          /*&& (!deletedAssignedRecId || (deletedAssignedRecId 
                && deletedAssignedRecId.toLowerCase().substring(0,15) != evt.recordId.toLowerCase().substring(0,15)))*/
          if(evt?.recordId && !donotAssignPrefixesToTask.includes(evt.recordId.substring(0, 3)) 
              && (["003", "00Q"].includes(evt.recordId.substring(0, 3)) 
                || !actEnabledWhatIdPrefixes?.length 
                || actEnabledWhatIdPrefixes.includes(evt.recordId.substring(0, 3)))) {        
            /*const whoIdPrefixes = ["003", "00Q"]; 
            let activeConnectedCallObj = incomingCallMapRef?.current?.get(activeConnectedCallIdRef?.current);
            if(activeConnectedCallObj) {
              if(!activeConnectedCallObj?.whoId && whoIdPrefixes.includes(evt.recordId.substring(0, 3))) {
                upsertIncomingCallMap(activeConnectedCallIdRef?.current, {whoId: evt.recordId, whoName: evt.recordName, whoType: evt.objectType});
              } else if(!activeConnectedCallObj?.whatId && !whoIdPrefixes.includes(evt.recordId.substring(0, 3))) {
                upsertIncomingCallMap(activeConnectedCallIdRef?.current, {whatId: evt.recordId, whatName: evt.recordName, whatType: evt.objectType});
              } else {
                assignMetaRef.current = { recordId: evt.recordId, recordName: evt.recordName, objectType: evt.objectType };
                setRecToConfirmBeforeAssignment({ recordId: evt.recordId, recordName: evt.recordName, objectType: evt.objectType });
              }
            } else {*/ // no need of doing assignment on ring for inbound
              let activeConnectedCallObj = callMapRef?.current?.get(activeConnectedCallIdRef?.current);
              if(activeConnectedCallObj && !activeConnectedCallObj.transferType) {
                if (deletedAssignedRecId?.toLowerCase().substring(0, 15) === evt.recordId.toLowerCase().substring(0, 15)) {
                  if (
                    (!activeConnectedCallObj.whoId || activeConnectedCallObj.whoId.substring(0, 15).toLowerCase() !== evt.recordId.substring(0, 15).toLowerCase()) &&
                    (!activeConnectedCallObj.whatId || activeConnectedCallObj.whatId.substring(0, 15).toLowerCase() !== evt.recordId.substring(0, 15).toLowerCase())
                  ) {
                    assignMetaRef.current = { recordId: evt.recordId, recordName: evt.recordName, objectType: evt.objectType };
                    setRecToConfirmBeforeAssignment({ recordId: evt.recordId, recordName: evt.recordName, objectType: evt.objectType });
                  } else {
                    // do nothing
                  }
                } else {
                  if(!activeConnectedCallObj.whoId && !activeConnectedCallObj.whatId 
                      && callMapRef.current.size == 1) { //auto assign if there is single active call, and no record has been assigned to it
                    if(sforce && sforce.opencti && (evt.recordId.substring(0, 3) == '003' || evt.recordId.substring(0, 3) == '500' 
                        || evt.recordId.substring(0, 3) == '00T' || evt.recordId.substring(0, 3) == '00U' 
                        || evt.recordId.substring(0, 3) == '00v')) {
                      try {
                        sforce.opencti.runApex({
                          apexClass: 'unrc.UnaricUnifyClientCtiController',
                          methodName: 'getRelatedRecordInfo',
                          methodParams: 'recordId=' + evt.recordId + '&recordName=' + evt.recordName + '&objectType=' + evt.objectType,
                          callback: (result) => {
                            if (result?.success && result?.returnValue?.runApex) {
                              const wrapperJson = result.returnValue.runApex;
                              const wrapper = wrapperJson ? JSON.parse(wrapperJson) : null;

                              if (wrapper && (wrapper.whoId || wrapper.whatId)) {
                                upsertCallMap(activeConnectedCallIdRef?.current, {
                                  whoId: wrapper.whoId, whoName: wrapper.whoName, whoType: wrapper.whoType,
                                  whatId: wrapper.whatId, whatName: wrapper.whatName, whatType: wrapper.whatType,
                                });
                              } else {
                                handleDefaultAssignment(evt);
                              }
                            } else {
                              console.error("page - Error calling Apex getRelatedRecordInfo:", result?.errors);
                              handleDefaultAssignment(evt);
                            }
                          }
                        });
                      } catch (ex) {
                        console.warn("pg - call to apex getRelatedRecordInfo Failed :", ex);
                        handleDefaultAssignment(evt);
                      }
                    } else {
                      handleDefaultAssignment(evt);
                    }
                  } else if((!activeConnectedCallObj.whoId 
                        || (activeConnectedCallObj.whoId 
                          && activeConnectedCallObj.whoId.substring(0, 15).toLowerCase() != evt.recordId.substring(0, 15).toLowerCase()))
                      && (!activeConnectedCallObj.whatId 
                        || (activeConnectedCallObj.whatId 
                          && activeConnectedCallObj.whatId.substring(0, 15).toLowerCase() != evt.recordId.substring(0, 15).toLowerCase()))) { //ask ques
                    assignMetaRef.current = { recordId: evt.recordId, recordName: evt.recordName, objectType: evt.objectType };
                    setRecToConfirmBeforeAssignment({ recordId: evt.recordId, recordName: evt.recordName, objectType: evt.objectType });
                  }
                  /*if(!activeConnectedCallObj?.whoId && whoIdPrefixes.includes(evt.recordId.substring(0, 3))) {
                    upsertCallMap(activeConnectedCallIdRef?.current, {whoId: evt.recordId, whoName: evt.recordName, whoType: evt.objectType});
                  } else if(!activeConnectedCallObj?.whatId && !whoIdPrefixes.includes(evt.recordId.substring(0, 3))) {
                    upsertCallMap(activeConnectedCallIdRef?.current, {whatId: evt.recordId, whatName: evt.recordName, whatType: evt.objectType});
                  } else {
                    assignMetaRef.current = { recordId: evt.recordId, recordName: evt.recordName, objectType: evt.objectType };
                  }*/
                }
              }
            //}
          } else { //clear old ques, as we are no longer on its record page
            setRecToConfirmBeforeAssignment({ recordId: null, recordName: null, objectType: null });
          }
        },
      });
    } catch (e) {
      console.warn("Failed to wire Salesforce page listeners:", e);
    }
  }, []);

  useEffect(() => {
    assignMetaRef.current = recToConfirmBeforeAssignment;
  }, [recToConfirmBeforeAssignment]);
 

  // Update softphone labels when call center settings are loaded
  useEffect(() => {
    setSoftphonePanelLabel({label: softphoneLabel});
    setSoftphoneItemLabel({label: softphoneLabel});
    setSoftphoneItemIcon("end_call");
  }, [softphoneLabel]);

  // Refresh session tokens periodically (server manages actual tokens)
  useEffect(() => {
    if (!isAuthenticated) return;

    const refreshTokens = async () => {
      try {
        const result = await refreshSession();
        
        if (!result.success) {
          if (result.error === "SESSION_EXPIRED") {
            console.log("Session expired, requiring re-authentication");
            setIsAuthenticated(false);
            setToken(null);
            setAccountInfo(null);
            setUserObjectId(null);
            setSoftphoneItemIcon("end_call");
          }
        }
      } catch (e) {
        console.error("Token refresh error:", e);
      }
    };

    // Refresh session every 5 minutes (server manages token expiry)
    const intervalId = setInterval(refreshTokens, 5 * 60 * 1000);
    return () => clearInterval(intervalId);
  }, [isAuthenticated]);

  // Listen for auth success from popup (session-based - no tokens passed)
  useEffect(() => {
    const handleMessage = async (event) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data || {};
      console.log("Auth message received:", data);
      
      // Session-based auth: popup just notifies success, we fetch session data from server
      if (data.type === "acs-auth-success") {
        try {
          const sessionResult = data.transferToken
            ? await completeSessionTransfer(data.transferToken)
            : await checkSession();
          const { authenticated, user } = sessionResult;
          
          if (authenticated && user) {
            setIsAuthenticated(true);
            setUserObjectId(user.userObjectId);
            setAccountInfo({ name: user.displayName, username: user.username, upn: user.upn,tenantId: user.tenantId });
            setSoftphoneItemIcon("call");

            // Get ACS token from session for initializing ACS SDK
            const acsTokenData = await getAcsToken();
            if (acsTokenData) {
              setToken({
                token: acsTokenData.token,
                expiresOn: acsTokenData.expiresOn,
              });
            }
          } else {
            console.error("Failed to initialize session after auth:", sessionResult.error);
          }
        } catch (e) {
          console.error("Failed to initialize session after auth:", e);
        }
      } else if (data.type === "acs-auth-error") {
        console.error("Auth error notification:", data.message);
        alert(data.message || "Authentication failed. Please check onboarding status.");
        window.location.reload();
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  useEffect(() => {

    if (isAuthenticated) {
      // Fail fast if sforce is not available
      if (typeof sforce === "undefined") {
        console.warn("sforce is not available, skipping Salesforce calls");
        return;
      }

      if (!accountInfo || !userObjectId) return;

      const { username, tenantId } = accountInfo || {};
      if (!username || !tenantId) return;

      fetchAndSendUserInfo(username, tenantId, userObjectId);
    }
  }, [accountInfo, userObjectId, isAuthenticated]);

  // Helper: call Salesforce Apex method
  const getLoggedInSfUserInfo = (className, methodName) => {
    return new Promise((resolve, reject) => {
      sforce.opencti.runApex({
        apexClass: className,
        methodName,
        callback: (result) => {
          if (result.success) {
            const userInfo = result.returnValue.runApex;
            console.log(`Result for ${methodName}:`, userInfo);
            resolve(userInfo);
          } else {
            console.error("Error:", result.errors);
            reject(result.errors);
          }
        },
      });
    });
  };

  // Consolidated function: fetch Salesforce info + send to backend in one call
  const fetchAndSendUserInfo = async (teamsUpn, tenantId, teamsUserId) => {
    try {
      // For Salesforce Values 
      const [
        sfUsername,
        sfUserId,
        sfFirstName,
        sfOrganizationName,
        // language,
        // locale,
        sfLastName,
        sfEmail,
        sfOrganizationId,
      ] = await Promise.all([
        getLoggedInSfUserInfo("UserInfo", "getUserName"),
        getLoggedInSfUserInfo("UserInfo", "getUserId"),
        getLoggedInSfUserInfo("UserInfo", "getFirstName"),
        getLoggedInSfUserInfo("UserInfo", "getOrganizationName"),
        // getLoggedInSfUserInfo("UserInfo", "getLanguage"),
        // getLoggedInSfUserInfo("UserInfo", "getLocale"),
        getLoggedInSfUserInfo("UserInfo", "getLastName"),
        getLoggedInSfUserInfo("UserInfo", "getUserEmail"),
        getLoggedInSfUserInfo("UserInfo", "getOrganizationId"),
      ]);

      const payload = {
        sfUserId,
        sfUsername,
        sfFirstName,
        sfLastName,
        sfEmail,
        orgId: sfOrganizationId,
        sfOrganizationName,
      };

      const response = await authenticatedFetch(
        '/api/db/userloggedin',
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        console.error("Getting Error to store User Mapping in Database");
        if (response.status === 401 || response.status === 403) {
          //  window.location.reload();
          dispatchToast(
            <Toast><ToastTitle>Your account has been deactivated. Please contact your administrator for assistance.</ToastTitle></Toast>,
            {
              intent: "error", timeout: 5000
            },
          );
          handleLogout();
        }
      }

      console.log("API call completed with combined payload");
    } catch (err) {
      console.error("Failed to fetch/send user info:", err);
    }
  };

  useEffect(() => {
    if(assignMetaRef.current != recToConfirmBeforeAssignment) {
      setRecToConfirmBeforeAssignment(assignMetaRef.current);
    }
  }, [assignMetaRef]);

  const handleLogin = () => {
    const w = 480,
      h = 640;
    const left = Math.round((screen.width - w) / 2);
    const top = Math.round((screen.height - h) / 2);
    window.open(
      "/login",
      "unaric_auth",
      `popup=yes,width=${w},height=${h},left=${left},top=${top}`
    );
  };

  const handleLogout = async () => {
    try {
      // Destroy server session (this also clears any remaining localStorage)
        await sessionLogout();
    } catch (err) {
      console.error("Session logout failed:", err);
    } finally {
      logout();
      setToken(null);
      setAccountInfo(null);
      setUserObjectId(null);
      setIsAuthenticated(false);
      setSoftphoneItemIcon("end_call");

      localStorage.removeItem("activePS");
    }
  };

  //const showIncomingRing = incomingCall && !call && callerInfo?.direction === "Inbound"; //ORIGINAL
  const showIncomingRing = incomingCallMap && incomingCallMap?.size > 0;

  /*console.log("callState", callState);
  const showActiveCall =
    call &&
    (callState === "Connecting" ||
      callState === "Connected" ||
      callState === "LocalHold" ||
      callState === "Ringing" ||
      callState === "EarlyMedia" ||
      callState === "RemoteHold");*/ //ORIGINAL
  const showActiveCall = callMap && callMap?.size > 0;

  useEffect(() => { //else Too many re-renders. React limits the number of renders to prevent an infinite loop.
    if (showIncomingRing && !showActiveCall) {
      setSelectedTab("inbound");
    } else if (!showIncomingRing && showActiveCall) {
      setSelectedTab("phone");
    } else if (!showIncomingRing && !showActiveCall) {
      setSelectedTab("phone");
    }
    if(!showActiveCall && recToConfirmBeforeAssignment) {
      setRecToConfirmBeforeAssignment({ recordId: null, recordName: null, objectType: null });
    }
  }, [showIncomingRing, showActiveCall]);

  return (
    <>
     <Toaster toasterId={toasterId} />
    {
      isAuthenticated ? ( <div className={styles.wrap}>
      
      {ErrorToaster}
      <div className={styles.root}>
        {/***************************/}
        {isAuthenticated && (
          <>
            <Card className={styles.card} appearance="filled">
              <div className={styles.header}>
                <div className={styles.presenceDiv}>
                  <PresenceIndicator />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px',width: "36%", }}>
                    {
                      accountInfo?.name && <Tooltip key="above" positioning="above" content={accountInfo?.upn ?? ""} relationship="label">
                        <Text weight="medium" className={styles.accountInfoText}> {accountInfo?.name}</Text>
                      </Tooltip>
                    }
                       
                    <Tooltip content="Logout" relationship="label">
                      <Button
                        appearance="transparent"
                        icon={<SignOutFilled />}
                        onClick={handleLogout}
                        aria-label="More options"
                      />
                    </Tooltip>
                  </div>
                </div>
                {/*<PresenceIndicator />*/}
              </div>
              
              <CardPreview>
                <Divider appearance="default" />
                <div>
                  {/***********tab : START**************/}
                  <TabList
                    selectedValue={selectedTab}
                    onTabSelect={(e, data) => setSelectedTab(data.value)}
                    aria-label="Softphone tabs"
                    className={styles.tabListFullWidth}
                  >
                    <Tab value="inbound" className={styles.tabEqual}
					disabled={
					  (() => {
						let isDisable = false;
						if(activeConnectedCallId) {
						  const activeConnectedCallObj = callMap?.get(activeConnectedCallId);
						  let activeConnectedCallTransferType = activeConnectedCallObj?.transferType;
						  isDisable = activeConnectedCallTransferType === "bt" || activeConnectedCallTransferType === "ct";
						}
						return isDisable;
					  })()
					}
					>Inbound{" "}
                      {/*<Tooltip content="Number of calls in Ringing State" relationship="label"> */}
                        <Body1 
                          className={mergeClasses(styles.required, selectedTab === "inbound" ? styles.boldText : styles.semiBoldText)} 
                          style={{ display: "inline-block", visibility: showIncomingRing && showActiveCall ? "visible" : "hidden" }}
                        >
                          {"(" + incomingCallMap?.size + ")"}
                        </Body1>                        
                      {/*</Tooltip>*/}
                    </Tab>
                    <Tab value="phone" className={styles.tabEqual}>Phone</Tab>
                    <Tab value="recent" className={styles.tabEqual}>Recent</Tab>
                  </TabList>
                  <Divider appearance="subtle" />
                  <div className={styles.panels} >
                    <div
                      id="panel-inbound"
                      role="tabpanel"
                      aria-labelledby="tab-inbound"
                      hidden={selectedTab !== "inbound"}
                      className={styles.panel}
                    >
                      {/*{isAuthenticated && showIncomingRing (
                      
                      Replace IncomingNotification -> IncomingCallToast*/}
                          <IncomingNotification
                            incomingCallMap={incomingCallMap}
                            onAcceptCall={acceptCall}
                            showIncomingRing={showIncomingRing}
                            onHangUp={hangUp}
                          />
                      {/*)}*/}
                    </div>
                    <div
                      id="panel-phone"
                      role="tabpanel"
                      aria-labelledby="tab-phone"
                      hidden={selectedTab !== "phone"}
                      className={styles.panel}
                    >
                       
                        {isAuthenticated && (
                          showActiveCall 
                          ? <ActiveCallScreen 
                              callMap={callMap}
                              upsertCallMap={upsertCallMap}
                              setActiveConnectedCallId={setActiveConnectedCallId}
                              activeConnectedCallId={activeConnectedCallId}
							                setWrapupConnectedCallId={setWrapupConnectedCallId} 
                              wrapupConnectedCallId={wrapupConnectedCallId} 
                              onWrapupDone={wrapupDone}
                              onWrapupEdit={wrapupEdit}
                              assignMetaRef={assignMetaRef} 
                              recToConfirmBeforeAssignment={recToConfirmBeforeAssignment}
                              setRecToConfirmBeforeAssignment={setRecToConfirmBeforeAssignment}
                              deletedAssignedRecIdRef={deletedAssignedRecIdRef}
                              setDeletedAssignedRecId={setDeletedAssignedRecId}
                              onHangUp={hangUp} 
                              //isMuted={isMuted}
                              onMute={mute}
                              onUnmute={unmute}
                              notes={notes}
                              onChangeNotes={onNotesInputChange}
                              callResult={callResult} 
                              onChangeCallResult={onCallResultInputChange}
                              callResultGroup={callResultGroup} 
							                onChangeCallResultGroup={onCallResultGroupInputChange}
                              isSwitchingCall={isSwitchingCall}
                              onBlindTransfer={blindTransfer}
                              onConsultedTransfer={consultedTransfer}
                              onCancelTransfer={cancelTransfer}
                              //callTransferState={callTransferState}
                              transferredCall={transferredCall}
                              //callerInfo={callerInfo}
                              //isHold={isHold}
                              onResume={onResume}
                              onHold={onHold} 
                              handleDtmf={handleDtmf}
                            />
                          : <PhonePanel
                              call={call}
                              incomingCall={incomingCall}
                              incomingCallMap={incomingCallMap}
                              callMap={callMap}
                              callState={callState}
                              onPlaceCall={placeCall}
                              /* errorMessage={errorMessage}*/
                            /> 
                        )}
                                                                        
                      
                    </div>
                    <div
                      id="panel-recent"
                      role="tabpanel"
                      aria-labelledby="tab-recent"
                      hidden={selectedTab !== "recent"}
                      className={styles.panel}
                    >
                      <CallHistoryProvider>
                        <HistoryList onPlaceCall={placeCall}/>
                      </CallHistoryProvider>
                    </div>
                  </div>
                  {/***********tab : END**************/}
                </div>
              </CardPreview>
            </Card>
          </>
        )}
        {/************************* */}
      </div>
    </div>):<SignIn onLogin={handleLogin} loginMessage={loginMessage} />
    }
    </>
  );
}

const useStyles = makeStyles({
  wrap: {
    maxWidth: "720px",
    margin: "0 auto",
    fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
  },
  notificationsContainer: {
    position: 'fixed',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)', // <-- centers the group
    display: 'flex',
    flexDirection: 'column',
    gap: 5,
    alignItems: 'center',
    zIndex: 1400, 
    maxHeight: 'auto',//'100vh',
    overflowY: 'auto',
    pointerEvents: 'none' // allow clicks only on children if needed
  },
  notificationItem: {
    width: 360, //NEW keep consistent width so stack is centered
    pointerEvents: 'auto', // enable clicks inside each card
  },
  //card start
  card: {
    maxWidth: "480px",
    width: "100%",
    margin: "0 auto",
    padding: "3%",
  },
  header: {
    //alignItems: "flex-start", //Added for agent name to appearon top, irrepective of oof msg
  },
  presenceDiv:{
    display: 'flex',
    justifyContent: 'space-between', 
    alignItems: 'center' 
  },
  accountInfoText: {
    wordBreak: "break-word",
    overflowWrap: "break-word"
  },
  //card end
  //START: tabs
  root: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    rowGap: "16px",//20px
    width: 'auto',
  },
  panels: {
    width: "100%", // panels fill the available width inside the root
    //for scroll within each tab
    maxHeight: "386px", //350px //w.r.t adapter xml
    height: "386px", //350px //w.r.t adapter xml
    overflowY: "auto",
  },
  panel: {
    /*padding: 12,
    borderRadius: 6,
    background: "var(--neutralBackground1, #fff)",
    boxShadow: "var(--shadow2, 0 1px 2px rgba(0,0,0,0.04))",
    width: "100%", // ensure panel content stretches to container
    boxSizing: "border-box",*/
  },

  tabListFullWidth: {
    display: "flex",
    width: "100%",
  },
  tabEqual: {
    flex: 1,                // each tab takes equal width
    justifyContent: "center", // center the label inside
  },
  //END: tabs
  //START: list
  boldText: {
    fontWeight: "bold",
  },
  semiBoldText: {
    fontWeight: tokens.fontWeightSemibold,
  },
  required: {
    color: "rgb(194, 57, 52)",
  },
  //END: list
});

export default App;